import { useEffect, useMemo, useState } from "react";
import {
  Alert, Avatar, Badge, Button, Card, Empty, Form, Input, InputNumber, List,
  Modal, Select, Space, Table, Tabs, Tag, Typography, message,
} from "antd";
import { Award, Megaphone, MessageSquare, Plus, Send, Sparkles, Trash2 } from "lucide-react";
import dayjs from "dayjs";
import axiosClient from "../../api/axiosClient";
import { comingSoonLabel } from "../../utils/comingSoon";
import { useAppSelector } from "../../store/hooks";

const { Text, Title } = Typography;

interface Thread {
  client_id: string;
  client_name: string;
  client_phone: string;
  last_message: string;
  last_sender: string;
  last_message_at: string;
  unread_count: number;
}

interface Msg {
  message_id: string;
  sender: "client" | "salon";
  body: string;
  created_at: string;
}

interface LoyaltyMember {
  client_id: string;
  name: string;
  phone: string;
  total_spend: number;
  visits: number;
  points: number;
  redeemed_points: number;
  available_points: number;
  tier: "bronze" | "silver" | "gold" | "platinum";
  last_visit?: string;
}

interface LoyaltySettings {
  points_per_dollar: number;
  silver_threshold: number;
  gold_threshold: number;
  platinum_threshold: number;
}

interface LoyaltyReward {
  reward_id: string;
  name: string;
  points_cost: number;
  active: boolean;
}

interface Campaign {
  campaign_id: string;
  name: string;
  channel: "email" | "sms";
  audience: string;
  subject: string;
  body: string;
  status: "draft" | "sent";
  sent_count: number;
  created_at: string;
}

const TIER_COLOR: Record<string, string> = { bronze: "default", silver: "cyan", gold: "gold", platinum: "purple" };
const AUDIENCE_LABELS: Record<string, string> = { all: "All Clients", inactive_90d: "Inactive 90+ days", new_30d: "New in last 30 days" };

// ── Inbox ────────────────────────────────────────────────────────────────────
function InboxTab() {
  const [threads, setThreads] = useState<Thread[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Thread | null>(null);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  function fetchThreads() {
    setLoading(true);
    axiosClient.get("/inbox").then((res) => setThreads(res.data ?? [])).catch(() => message.error("Failed to load inbox")).finally(() => setLoading(false));
  }

  useEffect(() => { fetchThreads(); }, []);

  function openThread(t: Thread) {
    setSelected(t);
    axiosClient.get(`/clients/${t.client_id}/messages`).then((res) => {
      setMessages(res.data ?? []);
      setThreads((prev) => prev.map((x) => (x.client_id === t.client_id ? { ...x, unread_count: 0 } : x)));
    });
  }

  async function sendReply() {
    if (!selected || !reply.trim()) return;
    setSending(true);
    try {
      await axiosClient.post(`/clients/${selected.client_id}/messages`, { body: reply.trim() });
      setReply("");
      openThread(selected);
      fetchThreads();
    } catch {
      message.error("Failed to send message");
    } finally {
      setSending(false);
    }
  }

  return (
    <div style={{ display: "flex", gap: 16, height: 560 }}>
      <Card size="small" style={{ width: 320, overflowY: "auto" }} loading={loading} bodyStyle={{ padding: 0 }}>
        {threads.length === 0 ? (
          <Empty description="No conversations yet" style={{ marginTop: 40 }} />
        ) : (
          <List
            dataSource={threads}
            renderItem={(t) => (
              <List.Item
                onClick={() => openThread(t)}
                style={{ cursor: "pointer", padding: 12, background: selected?.client_id === t.client_id ? "#f0f5ff" : undefined }}
              >
                <List.Item.Meta
                  avatar={<Avatar>{t.client_name.charAt(0)}</Avatar>}
                  title={<Space><span>{t.client_name}</span>{t.unread_count > 0 && <Badge count={t.unread_count} />}</Space>}
                  description={<Text ellipsis style={{ fontSize: 12, maxWidth: 220, display: "block" }}>{t.last_sender === "salon" ? "You: " : ""}{t.last_message}</Text>}
                />
              </List.Item>
            )}
          />
        )}
      </Card>

      <Card size="small" style={{ flex: 1, display: "flex", flexDirection: "column" }} bodyStyle={{ flex: 1, display: "flex", flexDirection: "column", padding: 12 }}>
        {!selected ? (
          <Empty description="Select a conversation" style={{ margin: "auto" }} />
        ) : (
          <>
            <Text strong style={{ marginBottom: 8 }}>{selected.client_name} · {selected.client_phone}</Text>
            <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8, padding: "8px 0" }}>
              {messages.map((m) => (
                <div key={m.message_id} style={{ alignSelf: m.sender === "salon" ? "flex-end" : "flex-start", maxWidth: "70%" }}>
                  <div style={{ background: m.sender === "salon" ? "#2563eb" : "#f0f0f0", color: m.sender === "salon" ? "#fff" : "#000", borderRadius: 12, padding: "8px 12px", fontSize: 13 }}>
                    {m.body}
                  </div>
                  <Text type="secondary" style={{ fontSize: 10 }}>{dayjs(m.created_at).format("MMM D, h:mm A")}</Text>
                </div>
              ))}
            </div>
            <Space.Compact style={{ width: "100%", marginTop: 8 }}>
              <Input placeholder="Type a reply…" value={reply} onChange={(e) => setReply(e.target.value)} onPressEnter={sendReply} />
              <Button type="primary" icon={<Send size={14} />} loading={sending} onClick={sendReply} />
            </Space.Compact>
          </>
        )}
      </Card>
    </div>
  );
}

// ── Loyalty ──────────────────────────────────────────────────────────────────
function LoyaltyTab({ isOwner }: { isOwner: boolean }) {
  const [members, setMembers] = useState<LoyaltyMember[]>([]);
  const [settings, setSettings] = useState<LoyaltySettings | null>(null);
  const [rewards, setRewards] = useState<LoyaltyReward[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rewardModalOpen, setRewardModalOpen] = useState(false);
  const [rewardForm] = Form.useForm();
  const [redeemFor, setRedeemFor] = useState<LoyaltyMember | null>(null);
  const [redeemRewardId, setRedeemRewardId] = useState<string | null>(null);
  const [redeeming, setRedeeming] = useState(false);

  function fetchAll() {
    setLoading(true);
    Promise.all([
      axiosClient.get("/loyalty/members"),
      isOwner ? axiosClient.get("/loyalty/settings") : Promise.resolve({ data: null }),
      axiosClient.get("/loyalty/rewards"),
    ]).then(([m, s, r]) => {
      setMembers(m.data ?? []);
      setSettings(s.data);
      setRewards(r.data ?? []);
    }).catch(() => message.error("Failed to load loyalty data")).finally(() => setLoading(false));
  }

  useEffect(() => { fetchAll(); }, [isOwner]);

  async function saveSettings() {
    if (!settings) return;
    setSaving(true);
    try {
      await axiosClient.put("/loyalty/settings", settings);
      message.success("Loyalty settings saved");
    } catch {
      message.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  }

  async function saveReward() {
    const vals = await rewardForm.validateFields();
    try {
      await axiosClient.post("/loyalty/rewards", vals);
      message.success("Reward added");
      setRewardModalOpen(false);
      rewardForm.resetFields();
      fetchAll();
    } catch {
      message.error("Failed to create reward");
    }
  }

  async function toggleReward(reward: LoyaltyReward) {
    try {
      await axiosClient.put(`/loyalty/rewards/${reward.reward_id}`, { ...reward, active: !reward.active });
      fetchAll();
    } catch {
      message.error("Failed to update reward");
    }
  }

  async function removeReward(id: string) {
    try {
      await axiosClient.delete(`/loyalty/rewards/${id}`);
      fetchAll();
    } catch {
      message.error("Failed to delete reward");
    }
  }

  async function confirmRedeem() {
    if (!redeemFor || !redeemRewardId) return;
    setRedeeming(true);
    try {
      await axiosClient.post(`/loyalty/members/${redeemFor.client_id}/redeem`, { reward_id: redeemRewardId });
      message.success("Reward redeemed");
      setRedeemFor(null);
      setRedeemRewardId(null);
      fetchAll();
    } catch (err: any) {
      message.error(err?.response?.data ?? "Failed to redeem reward");
    } finally {
      setRedeeming(false);
    }
  }

  const activeRewards = rewards.filter((r) => r.active);

  return (
    <div>
      {isOwner && settings && (
        <Card size="small" title="Points & Tiers" style={{ marginBottom: 16, maxWidth: 640 }}>
          <Space wrap size={16}>
            <div><Text type="secondary" style={{ fontSize: 12 }}>Points per $ spent</Text><br /><InputNumber min={0} step={0.5} value={settings.points_per_dollar} onChange={(v) => setSettings({ ...settings, points_per_dollar: v ?? 1 })} /></div>
            <div><Text type="secondary" style={{ fontSize: 12 }}>Silver at</Text><br /><InputNumber min={0} value={settings.silver_threshold} onChange={(v) => setSettings({ ...settings, silver_threshold: v ?? 0 })} /></div>
            <div><Text type="secondary" style={{ fontSize: 12 }}>Gold at</Text><br /><InputNumber min={0} value={settings.gold_threshold} onChange={(v) => setSettings({ ...settings, gold_threshold: v ?? 0 })} /></div>
            <div><Text type="secondary" style={{ fontSize: 12 }}>Platinum at</Text><br /><InputNumber min={0} value={settings.platinum_threshold} onChange={(v) => setSettings({ ...settings, platinum_threshold: v ?? 0 })} /></div>
          </Space>
          <div style={{ marginTop: 12 }}>
            <Button type="primary" size="small" loading={saving} onClick={saveSettings}>Save</Button>
          </div>
        </Card>
      )}

      {isOwner && (
        <Card
          size="small"
          title="Rewards Catalog"
          style={{ marginBottom: 16, maxWidth: 640 }}
          extra={<Button size="small" icon={<Plus size={13} />} onClick={() => { rewardForm.resetFields(); setRewardModalOpen(true); }}>Add Reward</Button>}
        >
          {rewards.length === 0 ? (
            <Text type="secondary" style={{ fontSize: 13 }}>No rewards yet — add one so staff can redeem points in person.</Text>
          ) : (
            <Space direction="vertical" size={8} style={{ width: "100%" }}>
              {rewards.map((r) => (
                <div key={r.reward_id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Space>
                    <Text style={{ opacity: r.active ? 1 : 0.5 }}>{r.name}</Text>
                    <Tag>{r.points_cost.toLocaleString()} pts</Tag>
                    {!r.active && <Tag>Inactive</Tag>}
                  </Space>
                  <Space>
                    <Button size="small" onClick={() => toggleReward(r)}>{r.active ? "Deactivate" : "Activate"}</Button>
                    <Button size="small" type="text" danger icon={<Trash2 size={13} />} onClick={() => removeReward(r.reward_id)} />
                  </Space>
                </div>
              ))}
            </Space>
          )}
        </Card>
      )}

      <Alert
        style={{ marginBottom: 16 }}
        type="info"
        showIcon
        message="Points and tiers are computed live from real completed-booking spend. Available points = earned minus redeemed — redemptions are a real ledger, not just a display number."
      />
      <Table
        rowKey="client_id"
        loading={loading}
        dataSource={members}
        columns={[
          { title: "Client", dataIndex: "name" },
          { title: "Tier", dataIndex: "tier", render: (t: string) => <Tag color={TIER_COLOR[t]}>{t.toUpperCase()}</Tag> },
          { title: "Available Points", dataIndex: "available_points", render: (v: number) => v.toLocaleString() },
          { title: "Lifetime Points", dataIndex: "points", render: (v: number) => v.toLocaleString() },
          { title: "Total Spend", dataIndex: "total_spend", render: (v: number) => `$${v.toFixed(2)}` },
          { title: "Visits", dataIndex: "visits" },
          { title: "Last Visit", dataIndex: "last_visit", render: (v?: string) => (v ? dayjs(v).format("MMM D, YYYY") : "—") },
          {
            title: "",
            key: "actions",
            render: (_: unknown, m: LoyaltyMember) => (
              <Button
                size="small"
                disabled={activeRewards.length === 0 || activeRewards.every((r) => r.points_cost > m.available_points)}
                onClick={() => { setRedeemFor(m); setRedeemRewardId(null); }}
              >
                Redeem
              </Button>
            ),
          },
        ]}
      />

      <Modal title="Add Reward" open={rewardModalOpen} onCancel={() => setRewardModalOpen(false)} onOk={saveReward} okText="Add">
        <Form form={rewardForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="name" label="Reward Name" rules={[{ required: true, message: "Required" }]}>
            <Input placeholder="e.g. Free Blowout" />
          </Form.Item>
          <Form.Item name="points_cost" label="Points Cost" rules={[{ required: true, message: "Required" }]}>
            <InputNumber min={1} style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Redeem for ${redeemFor?.name ?? ""}`}
        open={!!redeemFor}
        onCancel={() => setRedeemFor(null)}
        onOk={confirmRedeem}
        okText="Redeem"
        okButtonProps={{ disabled: !redeemRewardId, loading: redeeming }}
      >
        <Space direction="vertical" style={{ width: "100%", marginTop: 16 }}>
          <Text type="secondary">{redeemFor?.available_points.toLocaleString()} points available</Text>
          <Select
            style={{ width: "100%" }}
            placeholder="Choose a reward"
            value={redeemRewardId}
            onChange={setRedeemRewardId}
            options={activeRewards.map((r) => ({
              value: r.reward_id,
              label: `${r.name} (${r.points_cost.toLocaleString()} pts)`,
              disabled: !!redeemFor && r.points_cost > redeemFor.available_points,
            }))}
          />
        </Space>
      </Modal>
    </div>
  );
}

// ── Campaigns ────────────────────────────────────────────────────────────────
function CampaignsTab() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [form] = Form.useForm();

  function fetchCampaigns() {
    setLoading(true);
    axiosClient.get("/campaigns").then((res) => setCampaigns(res.data ?? [])).catch(() => message.error("Failed to load campaigns")).finally(() => setLoading(false));
  }

  useEffect(() => { fetchCampaigns(); }, []);

  async function saveCampaign() {
    const vals = await form.validateFields();
    try {
      await axiosClient.post("/campaigns", vals);
      message.success("Campaign created");
      setModalOpen(false);
      form.resetFields();
      fetchCampaigns();
    } catch {
      message.error("Failed to create campaign");
    }
  }

  async function removeCampaign(id: string) {
    try {
      await axiosClient.delete(`/campaigns/${id}`);
      fetchCampaigns();
    } catch {
      message.error("Failed to delete campaign");
    }
  }

  async function sendCampaign(id: string) {
    setSendingId(id);
    try {
      const res = await axiosClient.post(`/campaigns/${id}/send`);
      message.success(`Sent to ${res.data.sent_count} of ${res.data.audience_size} matching clients`);
      fetchCampaigns();
    } catch {
      message.error("Failed to send campaign");
    } finally {
      setSendingId(null);
    }
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
        <Text type="secondary">Email sends for real via your account's email service; SMS is logged to the server console (no SMS provider connected yet)</Text>
        <Button type="primary" icon={<Plus size={14} />} onClick={() => { form.resetFields(); setModalOpen(true); }}>New Campaign</Button>
      </div>
      {!loading && campaigns.length === 0 ? (
        <Empty description="No campaigns yet" />
      ) : (
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          {campaigns.map((c) => (
            <Card key={c.campaign_id} size="small">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <Space>
                    <Text strong>{c.name}</Text>
                    <Tag>{c.channel.toUpperCase()}</Tag>
                    <Tag color={c.status === "sent" ? "green" : "default"}>{c.status}</Tag>
                  </Space>
                  <div style={{ fontSize: 12, color: "#8c8c8c", marginTop: 4 }}>{AUDIENCE_LABELS[c.audience] ?? c.audience}{c.status === "sent" ? ` · sent to ${c.sent_count}` : ""}</div>
                </div>
                <Space>
                  {c.status === "draft" && (
                    <Button size="small" type="primary" icon={<Send size={13} />} loading={sendingId === c.campaign_id} onClick={() => sendCampaign(c.campaign_id)}>Send</Button>
                  )}
                  <Button size="small" type="text" danger icon={<Trash2 size={13} />} onClick={() => removeCampaign(c.campaign_id)} />
                </Space>
              </div>
            </Card>
          ))}
        </Space>
      )}

      <Modal title="New Campaign" open={modalOpen} onCancel={() => setModalOpen(false)} onOk={saveCampaign} okText="Create">
        <Form form={form} layout="vertical" style={{ marginTop: 16 }} initialValues={{ channel: "email", audience: "all" }}>
          <Form.Item name="name" label="Campaign Name" rules={[{ required: true, message: "Required" }]}>
            <Input placeholder="e.g. Fall Promo" />
          </Form.Item>
          <Space style={{ width: "100%" }} size={12}>
            <Form.Item name="channel" label="Channel" style={{ flex: 1 }}>
              <Select options={[{ value: "email", label: "Email" }, { value: "sms", label: "SMS" }]} />
            </Form.Item>
            <Form.Item name="audience" label="Audience" style={{ flex: 1 }}>
              <Select options={Object.entries(AUDIENCE_LABELS).map(([v, label]) => ({ value: v, label }))} />
            </Form.Item>
          </Space>
          <Form.Item name="subject" label="Subject (email only)">
            <Input placeholder="20% off this week" />
          </Form.Item>
          <Form.Item name="body" label="Message" rules={[{ required: true, message: "Required" }]}>
            <Input.TextArea rows={4} placeholder="Write your message…" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export default function AdminClientConnectPage() {
  const auth = useAppSelector((s) => s.auth);
  const isOwner = auth.role === "admin";

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: "#d1fae5", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <MessageSquare size={18} color="#059669" />
        </div>
        <div>
          <Title level={3} style={{ margin: 0 }}>Client Connect</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>Two-way messaging, loyalty, and marketing campaigns</Text>
        </div>
      </div>

      <Tabs
        items={[
          { key: "inbox", label: <Space size={6}><MessageSquare size={14} /> Inbox</Space>, children: <InboxTab /> },
          { key: "loyalty", label: <Space size={6}><Award size={14} /> Loyalty</Space>, children: <LoyaltyTab isOwner={isOwner} /> },
          { key: "campaigns", label: <Space size={6}><Megaphone size={14} /> Campaigns</Space>, children: <CampaignsTab /> },
          {
            key: "ai",
            label: comingSoonLabel(<Space size={6}><Sparkles size={14} /> AI Tools</Space>),
            disabled: true,
            children: (
              <Alert
                type="warning"
                showIcon
                message="AI win-back nudges and the AI message generator aren't built yet"
                description="Both need a real LLM API integration that isn't configured in this backend — the same category of gap as AI Telephony. Flagging rather than faking it."
              />
            ),
          },
        ]}
      />
    </div>
  );
}
